import React, { useState, useRef, JSX } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';

import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import { BackstoryElementProps } from './BackstoryTab';

import * as Types from 'types/types';
import { JobCreatorForm } from './JobCreatorForm';
import { JobCreatorResult } from './JobCreatorResult';
import {
  documentTypeFromFileName,
  JobProcessingPhase,
  previewDetailsFromMarkdown,
} from './jobCreatorUtils';

interface JobCreatorProps extends BackstoryElementProps {
  onSave?: (job: Types.Job) => void;
}
const JobCreator = (props: JobCreatorProps): JSX.Element => {
  const { user, apiClient } = useAuth();
  const { onSave } = props;
  const { setSnack } = useAppState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [jobDescription, setJobDescription] = useState<string>('');
  const [jobRequirements, setJobRequirements] = useState<Types.JobRequirements | null>(null);
  const [jobTitle, setJobTitle] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [summary, setSummary] = useState<string>('');
  const [job, setJob] = useState<Types.Job | null>(null);
  const [jobStatus, setJobStatus] = useState<string>('');
  const [jobStatusType, setJobStatusType] = useState<Types.ApiActivityType | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingPhase, setProcessingPhase] = useState<JobProcessingPhase>('idle');
  const [thinkingMarkdown, setThinkingMarkdown] = useState<string>('');
  const [generatedMarkdown, setGeneratedMarkdown] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetProcessingFeedback = (): void => {
    setProcessingPhase('idle');
    setThinkingMarkdown('');
    setGeneratedMarkdown('');
    setJobStatus('');
    setJobStatusType(null);
  };

  const applyMarkdownPreview = (markdown: string): void => {
    setGeneratedMarkdown(markdown);
    const preview = previewDetailsFromMarkdown(markdown);

    if (preview.title) {
      setJobTitle(current => current || preview.title || '');
    }
    if (preview.company) {
      setCompany(current => current || preview.company || '');
    }
    if (preview.summary) {
      setSummary(current => current || preview.summary || '');
    }
  };

  const jobStatusHandlers = {
    onStatus: (status: Types.ChatMessageStatus): void => {
      console.log('status:', status.content);
      setJobStatusType(status.activity);
      if (status.activity === 'converting') {
        setJobStatus(status.content);
        setProcessingPhase('markdown');
      } else if (status.activity === 'searching' || status.activity === 'generating') {
        setJobStatus(status.content);
        setProcessingPhase('requirements');
      } else if (status.activity === 'thinking') {
        setJobStatus('Thinking through the job analysis...');
        setThinkingMarkdown(status.content);
      } else {
        setJobStatus(status.content);
      }
    },
    onStreaming: (chunk: Types.ChatMessageStreaming): void => {
      setProcessingPhase('markdown');
      applyMarkdownPreview(chunk.content);
    },
    onMessage: (jobMessage: Types.JobRequirementsMessage): void => {
      const job: Types.Job = jobMessage.job;
      console.log('onMessage - job', job);
      setJob(job);
      setCompany(job.company || '');
      setJobDescription(job.description);
      setSummary(job.summary || '');
      setJobTitle(job.title || '');
      setJobRequirements(job.requirements || null);
      setGeneratedMarkdown(job.description);
      setJobStatusType(null);
      setJobStatus('');
    },
    onError: (error: Types.ChatMessageError): void => {
      console.log('onError', error);
      setSnack(error.content, 'error');
      setIsProcessing(false);
    },
    onComplete: (): void => {
      setJobStatusType(null);
      setJobStatus('');
      setIsProcessing(false);
    },
  };

  const handleJobUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const docType: Types.DocumentType | null = documentTypeFromFileName(file.name);

      if (!docType) {
        setSnack('Invalid file type. Please upload .txt, .md, .docx, or .pdf files only.', 'error');
        return;
      }

      try {
        setIsProcessing(true);
        setJob(null);
        resetProcessingFeedback();
        setJobDescription('');
        setJobTitle('');
        setCompany('');
        setJobRequirements(null);
        setSummary('');
        const controller = apiClient.createJobFromFile(file, jobStatusHandlers);
        const job = await controller.promise;
        if (!job) {
          return;
        }
        console.log(`Job id: ${job.id}`);
        e.target.value = '';
      } catch (error) {
        console.error(error);
        setSnack('Failed to upload document', 'error');
        setIsProcessing(false);
      }
    }
  };

  const handleUploadClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleSave = async (): Promise<void> => {
    const newJob: Types.Job = {
      ownerId: user?.id || '',
      ownerType: 'candidate',
      description: jobDescription,
      company: company,
      summary: summary,
      title: jobTitle,
      requirements: jobRequirements || undefined,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    setIsProcessing(true);
    const job = await apiClient.createJob(newJob);
    setIsProcessing(false);
    if (!job) {
      setSnack('Failed to save job', 'error');
      return;
    }
    onSave && onSave(job);
  };

  const handleExtractRequirements = async (): Promise<void> => {
    try {
      setIsProcessing(true);
      setJob(null);
      resetProcessingFeedback();
      const controller = apiClient.createJobFromDescription(jobDescription, jobStatusHandlers);
      const job = await controller.promise;
      if (!job) {
        setIsProcessing(false);
        return;
      }
      console.log(`Job id: ${job.id}`);
    } catch (error) {
      console.error(error);
      setSnack('Failed to upload document', 'error');
      setIsProcessing(false);
    }
    setIsProcessing(false);
  };

  return (
    <Box
      className="JobManagement"
      sx={{
        background: 'white',
        p: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {job === null && (
        <JobCreatorForm
          fileInputRef={fileInputRef}
          isMobile={isMobile}
          isProcessing={isProcessing}
          jobDescription={jobDescription}
          jobTitle={jobTitle}
          company={company}
          summary={summary}
          jobRequirements={jobRequirements}
          jobStatus={jobStatus}
          jobStatusType={jobStatusType}
          thinkingMarkdown={thinkingMarkdown}
          generatedMarkdown={generatedMarkdown}
          processingPhase={processingPhase}
          onUploadClick={handleUploadClick}
          onJobUpload={handleJobUpload}
          onExtractRequirements={handleExtractRequirements}
          onJobDescriptionChange={setJobDescription}
          onJobTitleChange={setJobTitle}
          onCompanyChange={setCompany}
        />
      )}
      {job && (
        <JobCreatorResult
          job={job}
          isMobile={isMobile}
          isProcessing={isProcessing}
          jobTitle={jobTitle}
          company={company}
          jobDescription={jobDescription}
          onSave={handleSave}
        />
      )}
    </Box>
  );
};

export { JobCreator };
